import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { Community, User } from '@/types';
import { Feed } from './components/Feed';
import { UpcomingEventsSection } from './components/UpcomingEventsSection';
import {
  InformativePostCard,
  type InformativePost,
} from '@/components/informative/InformativePostCard';
import useSWR from 'swr';
import axiosInstance from '@/lib/axios';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import { motion } from 'framer-motion';
import { Cake, Confetti } from '@phosphor-icons/react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { WhatsAppIcon } from '@/components/icons/whatsapp-icon';
import { Heart, History, Loader2, MessageSquare, PartyPopper, Phone, Wine } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { getInformativePostLabelClasses } from '@/lib/informative-post-label-styles';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  SegmentedTabs,
  SegmentedTabsList,
  SegmentedTabsTrigger,
} from '@/components/ui/segmented-tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { useUser } from '@/hooks/use-user';

interface SocialViewProps {
  community: Community;
  unreadPostIds?: string[];
  onUnreadViewed?: (postId: string) => void;
}

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);
const isBirthday = (dob: string | null) => {
  if (!dob) return false;
  const date = new Date(dob);
  const today = new Date();
  return date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
};

const isAnniversary = (anniversaryDate: string | null) => {
  if (!anniversaryDate) return false;
  const date = new Date(anniversaryDate);
  const today = new Date();
  return date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
};

type CelebrationRange = 'today' | 'week' | 'month' | 'next7';
const CELEBRATION_RANGES = ['today', 'week', 'month', 'next7'] as const;
type CelebrationKind = 'birthday' | 'anniversary';
const CELEBRATION_KINDS = ['birthday', 'anniversary'] as const;

function startOfLocalDay(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function startOfWeekMonday(today: Date) {
  const start = startOfLocalDay(today);
  const weekday = start.getDay();
  const offset = weekday === 0 ? -6 : 1 - weekday;
  return addDays(start, offset);
}

function eachDay(start: Date, end: Date) {
  const days: Date[] = [];
  for (let cursor = startOfLocalDay(start); cursor <= end; cursor = addDays(cursor, 1)) {
    days.push(cursor);
  }
  return days;
}

function isLastSevenDaysOfMonth(today = new Date()) {
  const startToday = startOfLocalDay(today);
  const daysInMonth = new Date(startToday.getFullYear(), startToday.getMonth() + 1, 0).getDate();
  return startToday.getDate() >= daysInMonth - 6;
}

function rangeDates(range: CelebrationRange, today = new Date()) {
  const startToday = startOfLocalDay(today);
  if (range === 'today') return [startToday];
  if (range === 'week') {
    const start = startOfWeekMonday(startToday);
    return eachDay(start, addDays(start, 6));
  }
  if (range === 'next7') return eachDay(addDays(startToday, 1), addDays(startToday, 7));
  return eachDay(
    new Date(today.getFullYear(), today.getMonth(), 1),
    new Date(today.getFullYear(), today.getMonth() + 1, 0)
  );
}

function emptyCelebrationCopy(kind: CelebrationKind, range: CelebrationRange) {
  const label = kind === 'birthday' ? 'birthdays' : 'anniversaries';
  if (range === 'today') return `No ${label} today.`;
  if (range === 'week') return `No ${label} this week.`;
  if (range === 'next7') return `No ${label} in the next 7 days.`;
  return `No ${label} this month.`;
}

function monthDayKey(date: Date) {
  return `${date.getMonth()}-${date.getDate()}`;
}

function celebrationMonthDay(value: string | null) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${parsed.getMonth()}-${parsed.getDate()}`;
}

function daysSinceLastOccasion(value: string | null, today = new Date()) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const startToday = startOfLocalDay(today);
  let occasion = new Date(startToday.getFullYear(), parsed.getMonth(), parsed.getDate());
  if (occasion > startToday) {
    occasion = new Date(startToday.getFullYear() - 1, parsed.getMonth(), parsed.getDate());
  }
  return Math.round((startToday.getTime() - occasion.getTime()) / 86_400_000);
}

function missedWishTiming(value: string | null) {
  const days = daysSinceLastOccasion(value);
  if (days == null) return null;
  if (days <= 1) return 'yesterday';
  return `${days} days ago`;
}

function formatOccasionBadge(type: CelebrationKind, date: Date, today = new Date()) {
  const isToday = date.getMonth() === today.getMonth() && date.getDate() === today.getDate();
  const label = type === 'birthday' ? 'Birthday' : 'Anniversary';
  if (isToday) return `${label} Today`;
  const formatted = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  return `${label} · ${formatted}`;
}

type CelebrationOccasion = 'birthday' | 'anniversary';

type CelebrationListUser = User & { spouseId?: string | null };

type CelebrationEntry = {
  member: CelebrationListUser;
  type: CelebrationKind;
  date: Date;
  index: number;
};

function celebrationEntryKey(entry: CelebrationEntry) {
  return `${entry.member.id}-${entry.type}`;
}

function celebrationRows(entries: CelebrationEntry[]) {
  const seen = new Set<string>();
  const rows: Array<
    | { type: 'single'; entry: CelebrationEntry }
    | { type: 'pair'; left: CelebrationEntry; right: CelebrationEntry }
  > = [];

  for (const entry of entries) {
    const key = celebrationEntryKey(entry);
    if (seen.has(key)) continue;
    const spouseId = entry.member.spouseId;
    const spouseEntries = spouseId
      ? entries.filter(
          (candidate) =>
            candidate.member.id === spouseId && !seen.has(celebrationEntryKey(candidate))
        )
      : [];
    const spouseEntry =
      spouseEntries.find((candidate) => candidate.type === entry.type) ?? spouseEntries[0];
    if (spouseEntry) {
      seen.add(key);
      seen.add(celebrationEntryKey(spouseEntry));
      rows.push({ type: 'pair', left: entry, right: spouseEntry });
      continue;
    }
    seen.add(key);
    rows.push({ type: 'single', entry });
  }

  return rows;
}

interface CelebrationMember {
  id: string;
  firstName: string | null;
  lastName: string | null;
  image: string | null;
  phone: string | null;
  dob: string | null;
  anniversaryDate: string | null;
}

function belatedBirthdayWishes(firstName: string) {
  return [
    `Hey ${firstName}! 🎂 I'm a little late, but happy belated birthday! Hope you had an amazing day and have an even better year ahead!`,
    `Belated happy birthday, ${firstName}! 🎉 Hope your day was filled with happiness, laughter, and lots of cake. Wishing you an awesome year ahead!`,
    `Hey ${firstName}! 😊 Sorry I missed your birthday! Sending you lots of good wishes for the year ahead. Hope it's your best one yet!`,
    `Happy belated birthday, ${firstName}! 🎁 A little late to the party, but wishing you loads of happiness, success, and exciting adventures this year!`,
    `${firstName}, wishing you a fantastic year ahead! 🎂 Sorry I missed your special day. Hope you had a wonderful birthday and an even more wonderful year ahead!`,
  ];
}

function belatedAnniversaryWishes(firstName: string): string[] {
  return [
    `Hey ${firstName}! ❤️ Belated happy anniversary to you both! Wishing you many more years of love, laughter, and beautiful memories together.`,
    `Happy belated anniversary, ${firstName}! 🥂 Hope you both had a lovely day. Here's to many more happy years together!`,
    `Hey ${firstName}! 💕 Sorry I missed your anniversary! Wishing you both a lifetime of love, happiness, and countless reasons to celebrate.`,
    `Belated anniversary wishes, ${firstName}! ✨ May your journey together keep getting more beautiful with every passing year. Wishing you both lots of happiness!`,
    `${firstName}, happy belated anniversary to you both! 🎉 Hope you had a wonderful celebration and here's to many more amazing milestones together!`,
  ];
}

function advanceBirthdayWishes(firstName: string) {
  return [
    `Hey ${firstName}! 🎂 Your birthday is coming up! Wishing you a wonderful day ahead and a year full of joy.`,
    `Advance happy birthday, ${firstName}! 🎈 Hope your special day is filled with love, laughter, and everything you love.`,
    `Hey ${firstName}! 🎁 Just a little early to say happy birthday! Wishing you an amazing year ahead.`,
    `Happy birthday in advance, ${firstName}! ✨ May the days ahead bring you success, smiles, and sweet surprises.`,
    `${firstName}, sending an early birthday wish! 🎉 Hope your celebration is as wonderful as you are.`,
  ];
}

function advanceAnniversaryWishes(firstName: string) {
  return [
    `Hey ${firstName}! ❤️ Your anniversary is coming up! Wishing you both a beautiful celebration and many more happy years.`,
    `Advance happy anniversary, ${firstName}! 🥂 Hope you both have a lovely day together.`,
    `Hey ${firstName}! ✨ A little early, but happy anniversary to you both! Wishing you love and laughter ahead.`,
    `Happy anniversary in advance, ${firstName}! 💕 May your journey together keep getting more beautiful.`,
    `${firstName}, sending early anniversary wishes to you both! 🎉 Here's to many more wonderful years together.`,
  ];
}

function todayBirthdayWishes(firstName: string) {
  return [
    `Hey ${firstName}! 🎂 Wishing you a fantastic birthday! Hope your day is as wonderful as you are.`,
    `Happy Birthday, ${firstName}! 🎈 May your year ahead be filled with joy, success, and amazing moments. Enjoy your special day!`,
    `Wishing you a very Happy Birthday, ${firstName}! 🎊 Stay blessed and keep shining. Cheers to another year of great memories!`,
    `Hey ${firstName}! 🎁 Sending you big birthday vibes! May this year bring you closer to all your dreams. Have an awesome one!`,
    `Happy Birthday, ${firstName}! ✨ Hope you have a day filled with love, laughter, and lots of cake!`,
  ];
}

function todayAnniversaryWishes(firstName: string) {
  return [
    `Hey ${firstName}! 🎊 Happy Anniversary! Wishing you both a lifetime of love and happiness. Cheers to many more!`,
    `Happy Anniversary, ${firstName}! 🥂 May your journey together continue to be filled with love and laughter. Have a wonderful celebration!`,
    `Wishing you both a very Happy Anniversary, ${firstName}! ✨ May your bond grow stronger with each passing year. Cheers to your love!`,
    `Congrats on another beautiful year together, ${firstName}! ❤️ Wishing you a day and a lifetime of special memories. Happy Anniversary!`,
    `Hey ${firstName}! 🌟 Happy Anniversary! It's so inspiring to see your journey together. Wishing you endless happiness and joy!`,
  ];
}

function MissedWishDialog({
  open,
  onOpenChange,
  member,
  isBirthdayWish,
  timing,
  onSend,
  upcoming = false,
  today = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: { firstName?: string | null; lastName?: string | null; image?: string | null } | null;
  isBirthdayWish: boolean;
  timing: string | null;
  onSend: () => void;
  upcoming?: boolean;
  today?: boolean;
}) {
  const name = [member?.firstName, member?.lastName].filter(Boolean).join(' ') || 'them';
  const title = today
    ? isBirthdayWish
      ? 'A wish for today'
      : 'Wish Anniversary today'
    : upcoming
      ? isBirthdayWish
        ? 'A wish can arrive early'
        : 'Wish Anniversary in advance'
      : isBirthdayWish
        ? 'A wish can still sparkle'
        : 'Missed to wish Anniversary';
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-[440px] overflow-visible border-0 bg-transparent p-0 shadow-none sm:rounded-none">
        <div className="relative px-5 py-6">
          <div
            aria-hidden
            className="absolute left-1 top-10 h-24 w-24 rounded-full bg-primary/20 blur-2xl"
          />
          <div
            aria-hidden
            className="absolute bottom-8 right-1 h-28 w-28 rounded-full bg-primary/15 blur-2xl"
          />
          <div className="relative overflow-hidden rounded-3xl border border-primary/10 bg-white px-6 pb-6 pt-8 shadow-xl shadow-primary/15">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/10 via-white to-primary/[0.04]"
            />
            <img
              src="/celebration-stickers/balloons.png"
              alt=""
              className="pointer-events-none absolute -left-3 top-1 z-0 h-36 w-auto opacity-40 mix-blend-multiply"
            />
            {timing && (
              <span className="absolute right-4 top-4 z-20 rounded-full bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground shadow-sm shadow-primary/25">
                {timing}
              </span>
            )}
            <AlertDialogHeader className="relative z-10 items-center space-y-3 text-center sm:text-center">
              <Avatar className="h-[4.5rem] w-[4.5rem]">
                <AvatarImage src={member?.image || undefined} alt={name} className="object-cover" />
                <AvatarFallback className="bg-primary/10 text-lg font-bold text-primary">
                  {name
                    .split(' ')
                    .map((part) => part[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <AlertDialogTitle className="flex items-center justify-center gap-2 font-outfit text-2xl font-black tracking-tight text-foreground">
                {title}
                {isBirthdayWish ? (
                  <Cake weight="fill" className="h-6 w-6 shrink-0 text-pink-500" />
                ) : (
                  <PartyPopper className="h-6 w-6 shrink-0 text-primary" />
                )}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[15px] leading-relaxed text-foreground/80">
                {today ? (
                  isBirthdayWish ? (
                    <>
                      <span className="font-bold text-primary">{name}</span>
                      &apos;s <span className="font-bold text-primary">birthday</span> is today, and
                      a warm note can light up their day.
                      <span className="mt-1 block">Want to send a little joy on WhatsApp?</span>
                    </>
                  ) : (
                    <>
                      <span className="font-bold text-primary">{name}</span>
                      &apos;s <span className="font-bold text-primary">anniversary</span> is today,
                      and a sweet wish would make them smile.{' '}
                      <Wine className="inline h-4 w-4 text-primary" />
                      <span className="mt-1 block">Want to send one now?</span>
                    </>
                  )
                ) : upcoming ? (
                  isBirthdayWish ? (
                    <>
                      <span className="font-bold text-primary">{name}</span>
                      &apos;s <span className="font-bold text-primary">birthday</span> is coming up,
                      and an early note can make their day even brighter.
                      <span className="mt-1 block">Want to send a little joy on WhatsApp?</span>
                    </>
                  ) : (
                    <>
                      <span className="font-bold text-primary">{name}</span>
                      &apos;s <span className="font-bold text-primary">anniversary</span> is coming
                      up, and an early wish would still make them smile.{' '}
                      <Wine className="inline h-4 w-4 text-primary" />
                      <span className="mt-1 block">Want to send one now?</span>
                    </>
                  )
                ) : isBirthdayWish ? (
                  <>
                    <span className="font-bold text-primary">{name}</span>
                    &apos;s <span className="font-bold text-primary">birthday</span> slipped by, but
                    a warm note can still light up their day.
                    <span className="mt-1 block">Want to send a little joy on WhatsApp?</span>
                  </>
                ) : (
                  <>
                    <span className="font-bold text-primary">{name}</span>
                    &apos;s <span className="font-bold text-primary">anniversary</span> slipped by,
                    and a sweet wish would still make them smile.{' '}
                    <Wine className="inline h-4 w-4 text-primary" />
                    <span className="mt-1 block">Want to send one now?</span>
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="relative z-10 mt-6 gap-2 sm:justify-center sm:space-x-0">
              <AlertDialogCancel className="rounded-full border-primary/15 bg-white/90 px-5 text-foreground hover:bg-primary/5">
                Maybe later
              </AlertDialogCancel>
              <AlertDialogAction
                className="rounded-full border-0 bg-primary px-5 text-primary-foreground shadow-lg shadow-primary/25 hover:bg-primary/90"
                onClick={onSend}
              >
                <span className="inline-flex items-center gap-1.5">
                  {isBirthdayWish ? 'Send a wish' : 'Wish them now'}
                  {isBirthdayWish && <PartyPopper className="h-4 w-4" />}
                </span>
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function CelebrationItem({
  member,
  type,
  occasionDate,
  snug = false,
  onNameClick,
}: {
  member: User;
  type: CelebrationKind;
  occasionDate: Date;
  snug?: boolean;
  onNameClick?: () => void;
}) {
  const navigate = useNavigate();
  const { communityId } = useParams();
  const firstName = member.firstName || 'Friend';
  const phone = member.phone || '';
  const cleanPhone = phone.replace(/\D/g, '');

  const openProfile = () => {
    const profilePath = `/u/${member.id}`;
    navigate(communityId ? `/${communityId}${profilePath}` : profilePath);
  };

  const openWhatsApp = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    const wishes =
      type === 'birthday' ? todayBirthdayWishes(firstName) : todayAnniversaryWishes(firstName);
    const randomWish = wishes[Math.floor(Math.random() * wishes.length)];
    event.currentTarget.href = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(randomWish)}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      className={cn(
        'flex items-center justify-between gap-3 bg-white px-4 transition-colors duration-300 group hover:bg-primary/[0.03]',
        snug ? 'py-1.5' : 'py-3'
      )}
    >
      <button
        type="button"
        onClick={openProfile}
        className="relative shrink-0"
        title="Open profile"
      >
        <div className="relative group">
          <Avatar className="h-14 w-14 border-2 border-primary/20 p-0.5 shadow-sm ring-4 ring-primary/5 transition-transform duration-500 group-hover:scale-105">
            <AvatarImage src={member.image} alt={firstName} className="rounded-full object-cover" />
            <AvatarFallback className="bg-gradient-to-br from-primary/10 to-primary/5 font-display text-xl font-bold text-primary">
              {member.firstName?.[0] || 'U'}
            </AvatarFallback>
          </Avatar>
          <div className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-border/10 bg-white shadow-md dark:bg-stone-900">
            {type === 'birthday' ? (
              <Cake weight="fill" className="h-3.5 w-3.5 text-pink-500" />
            ) : (
              <Confetti weight="fill" className="h-3.5 w-3.5 text-yellow-500" />
            )}
          </div>
        </div>
      </button>
      <button
        type="button"
        onClick={onNameClick ?? openProfile}
        className="flex min-w-0 flex-1 items-center text-left"
      >
        <div className="min-w-0">
          <p className="mb-1 truncate text-lg font-extrabold leading-none tracking-tight text-foreground">
            {member.firstName} {member.lastName}
          </p>
          <span
            className={cn(
              'inline-block max-w-full truncate rounded-md px-1.5 py-0.5 text-[10px] font-black uppercase tracking-[0.1em]',
              type === 'birthday'
                ? 'bg-pink-500/5 text-pink-500/90'
                : 'bg-yellow-500/5 text-yellow-600/90'
            )}
          >
            {formatOccasionBadge(type, occasionDate)}
          </span>
        </div>
      </button>
      <div
        className="relative z-10 flex shrink-0 items-center gap-3"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        {phone && (
          <motion.div whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}>
            <a
              href={`tel:${phone}`}
              title="Call member"
              onClick={(event) => event.stopPropagation()}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#2563eb] text-white shadow-lg shadow-blue-500/25 transition-colors hover:bg-[#1d4ed8]"
            >
              <Phone className="h-5 w-5" />
            </a>
          </motion.div>
        )}
        {cleanPhone && (
          <motion.div whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}>
            <a
              href={`https://wa.me/${cleanPhone}`}
              target="_blank"
              rel="noopener noreferrer"
              title="WhatsApp member"
              onClick={openWhatsApp}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#25D366] text-white shadow-xl shadow-green-500/40 transition-colors hover:bg-[#128C7E]"
            >
              <WhatsAppIcon className="h-5 w-5" />
            </a>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export function SocialView({ community, unreadPostIds = [], onUnreadViewed }: SocialViewProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [missedWishOpen, setMissedWishOpen] = useState(false);
  const [cardWish, setCardWish] = useState<{
    member: CelebrationListUser;
    type: CelebrationKind;
    timing: 'upcoming' | 'missed' | 'today';
    timingLabel: string | null;
  } | null>(null);
  const [celebrationRange, setCelebrationRange] = useQueryState(
    'range',
    parseAsStringLiteral(CELEBRATION_RANGES).withDefault('today').withOptions({
      history: 'replace',
      clearOnDefault: true,
    })
  );
  const showNextSevenDays = isLastSevenDaysOfMonth();
  const activeCelebrationRange =
    celebrationRange === 'next7' && !showNextSevenDays ? 'today' : celebrationRange;

  useEffect(() => {
    if (celebrationRange === 'next7' && !showNextSevenDays) {
      void setCelebrationRange('today');
    }
  }, [celebrationRange, showNextSevenDays, setCelebrationRange]);

  const [celebrationKind, setCelebrationKind] = useQueryState(
    'occasion',
    parseAsStringLiteral(CELEBRATION_KINDS).withDefault('birthday').withOptions({
      history: 'replace',
      clearOnDefault: true,
    })
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyPosts, setHistoryPosts] = useState<InformativePost[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [historyHasMore, setHistoryHasMore] = useState(true);
  const [locallyViewedIds, setLocallyViewedIds] = useState<Set<string>>(() => new Set());
  const [feedSessionId, setFeedSessionId] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [unreadFocusToken, setUnreadFocusToken] = useState(0);
  const [newerCount, setNewerCount] = useState(0);

  const { user } = useUser();
  const isMember = user?.communities.some((c) => c.id === community.id) ?? false;

  const activeUnreadPostIds = useMemo(
    () => unreadPostIds.filter((id) => !locallyViewedIds.has(id)),
    [unreadPostIds, locallyViewedIds]
  );

  const handleUnreadViewed = useCallback(
    (postId: string) => {
      setLocallyViewedIds((prev) => {
        if (prev.has(postId)) return prev;
        const next = new Set(prev);
        next.add(postId);
        return next;
      });
      onUnreadViewed?.(postId);
    },
    [onUnreadViewed]
  );

  useEffect(() => {
    if (!feedSessionId || !isMember) {
      setNewerCount(0);
      return;
    }

    let cancelled = false;

    const pollFreshness = async () => {
      try {
        const res = await axiosInstance.get(
          `/api/communities/${community.id}/posts/feed/freshness`,
          { params: { sessionId: feedSessionId } }
        );
        if (!cancelled) {
          setNewerCount(Number(res.data.newerCount) || 0);
        }
      } catch {
        /* ignore poll errors */
      }
    };

    void pollFreshness();
    const interval = window.setInterval(pollFreshness, 45000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [community.id, feedSessionId, isMember]);

  const celebrationUserId = searchParams.get('userId');
  const celebrationParam = searchParams.get('celebration');
  const celebrationOccasion: CelebrationOccasion | null =
    celebrationParam === 'birthday' || celebrationParam === 'anniversary' ? celebrationParam : null;

  const { data: celebrationData, isLoading: celebrationsLoading } = useSWR<{
    celebrations: CelebrationListUser[];
  }>(
    community.showCelebrations || (celebrationUserId && celebrationOccasion)
      ? `/api/communities/${community.id}/celebrations`
      : null,
    fetcher
  );
  const { data: informativeData, isLoading: informativeLoading } = useSWR(
    isMember ? `/api/communities/${community.id}/informative-posts` : null,
    fetcher
  );

  const celebrations = celebrationData?.celebrations || [];
  const birthdayMembers = useMemo(
    () => celebrations.filter((m) => isBirthday(m.dob ?? null)),
    [celebrations]
  );
  const anniversaryMembers = useMemo(
    () => celebrations.filter((m) => isAnniversary(m.anniversaryDate ?? null)),
    [celebrations]
  );
  const celebrationEntries = useMemo(() => {
    const dates = rangeDates(activeCelebrationRange);
    const byKey = new Map(dates.map((date, index) => [monthDayKey(date), { date, index }]));
    const entries: CelebrationEntry[] = [];
    const seen = new Set<string>();
    for (const member of celebrations) {
      const birthday = byKey.get(celebrationMonthDay(member.dob ?? null) ?? '');
      const anniversary = byKey.get(celebrationMonthDay(member.anniversaryDate ?? null) ?? '');
      const birthdayKey = `${member.id}-birthday`;
      if (birthday && !seen.has(birthdayKey)) {
        seen.add(birthdayKey);
        entries.push({ member, type: 'birthday', date: birthday.date, index: birthday.index });
      }
      const anniversaryKey = `${member.id}-anniversary`;
      if (anniversary && !seen.has(anniversaryKey)) {
        seen.add(anniversaryKey);
        entries.push({
          member,
          type: 'anniversary',
          date: anniversary.date,
          index: anniversary.index,
        });
      }
    }
    entries.sort(
      (a, b) =>
        a.index - b.index || (a.member.firstName || '').localeCompare(b.member.firstName || '')
    );
    return entries;
  }, [celebrations, activeCelebrationRange]);
  const visibleCelebrationEntries = useMemo(
    () => celebrationEntries.filter((entry) => entry.type === celebrationKind),
    [celebrationEntries, celebrationKind]
  );
  const celebrationRowItems = useMemo(
    () => celebrationRows(visibleCelebrationEntries),
    [visibleCelebrationEntries]
  );
  const celebratingToday = celebrationUserId
    ? (celebrationOccasion === 'birthday' ? birthdayMembers : anniversaryMembers).some(
        (member) => member.id === celebrationUserId
      )
    : false;
  const needsMemberLookup = Boolean(
    celebrationUserId &&
    celebrationOccasion &&
    !celebrationsLoading &&
    celebrationData &&
    !celebratingToday
  );
  const { data: memberLookup } = useSWR<{ member: CelebrationMember | null }>(
    needsMemberLookup
      ? `/api/communities/${community.id}/celebrations?userId=${celebrationUserId}`
      : null,
    fetcher
  );
  const missedMember = memberLookup?.member ?? null;

  const clearCelebrationParams = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('userId');
    next.delete('celebration');
    setSearchParams(next, { replace: true });
    setMissedWishOpen(false);
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!needsMemberLookup || !missedMember || !celebrationOccasion) return;
    const stillToday =
      celebrationOccasion === 'birthday'
        ? isBirthday(missedMember.dob ?? null)
        : isAnniversary(missedMember.anniversaryDate ?? null);
    if (!stillToday) setMissedWishOpen(true);
  }, [needsMemberLookup, missedMember, celebrationOccasion]);

  const sendLateWish = () => {
    if (!missedMember || !celebrationOccasion) return;
    const cleanPhone = (missedMember.phone || '').replace(/\D/g, '');
    if (!cleanPhone) {
      toast.error('No WhatsApp number for this member');
      return;
    }
    const firstName = missedMember.firstName || 'Friend';
    const wishes =
      celebrationOccasion === 'birthday'
        ? belatedBirthdayWishes(firstName)
        : belatedAnniversaryWishes(firstName);
    const wish = wishes[Math.floor(Math.random() * wishes.length)];
    window.open(
      `https://wa.me/${cleanPhone}?text=${encodeURIComponent(wish)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const isBirthdayWish = celebrationOccasion !== 'anniversary';
  const missedTiming = missedWishTiming(
    isBirthdayWish ? (missedMember?.dob ?? null) : (missedMember?.anniversaryDate ?? null)
  );
  const latestInformativePost =
    isMember && (informativeData?.posts?.[0] as InformativePost | undefined);

  const fetchHistory = async (cursor?: string) => {
    if (cursor === undefined) {
      setHistoryLoading(true);
      setHistoryPosts([]);
      setHistoryCursor(null);
      setHistoryHasMore(true);
    } else {
      setHistoryLoading(true);
    }

    try {
      const response = await axiosInstance.get(
        `/api/communities/${community.id}/informative-posts?type=history${
          cursor ? `&cursor=${cursor}` : ''
        }`
      );
      const newPosts = response.data.posts;
      setHistoryPosts((prev) => (cursor ? [...prev, ...newPosts] : newPosts));
      setHistoryCursor(response.data.nextCursor);
      setHistoryHasMore(!!response.data.nextCursor);
    } catch {
      console.error('Failed to fetch history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = () => {
    setHistoryOpen(true);
    fetchHistory();
  };

  const sendCardWish = () => {
    if (!cardWish) return;
    const cleanPhone = (cardWish.member.phone || '').replace(/\D/g, '');
    if (!cleanPhone) {
      toast.error('No WhatsApp number for this member');
      return;
    }
    const firstName = cardWish.member.firstName || 'Friend';
    const wishes =
      cardWish.timing === 'missed'
        ? cardWish.type === 'birthday'
          ? belatedBirthdayWishes(firstName)
          : belatedAnniversaryWishes(firstName)
        : cardWish.timing === 'today'
          ? cardWish.type === 'birthday'
            ? todayBirthdayWishes(firstName)
            : todayAnniversaryWishes(firstName)
          : cardWish.type === 'birthday'
            ? advanceBirthdayWishes(firstName)
            : advanceAnniversaryWishes(firstName);
    const wish = wishes[Math.floor(Math.random() * wishes.length)];
    window.open(
      `https://wa.me/${cleanPhone}?text=${encodeURIComponent(wish)}`,
      '_blank',
      'noopener,noreferrer'
    );
    setCardWish(null);
  };

  const promptCardWish = (entry: CelebrationEntry) => {
    const today = startOfLocalDay(new Date());
    const occasion = startOfLocalDay(entry.date);
    if (occasion.getTime() === today.getTime()) {
      setCardWish({
        member: entry.member,
        type: entry.type,
        timing: 'today',
        timingLabel: 'today',
      });
      return;
    }
    const missed = occasion < today;
    const days = Math.round(Math.abs(today.getTime() - occasion.getTime()) / 86_400_000);
    setCardWish({
      member: entry.member,
      type: entry.type,
      timing: missed ? 'missed' : 'upcoming',
      timingLabel: missed
        ? days <= 1
          ? 'yesterday'
          : `${days} days ago`
        : days <= 1
          ? 'tomorrow'
          : `in ${days} days`,
    });
  };

  return (
    <div className="space-y-6 pb-8">
      <MissedWishDialog
        open={missedWishOpen}
        onOpenChange={(open) => {
          if (!open) clearCelebrationParams();
        }}
        member={missedMember}
        isBirthdayWish={isBirthdayWish}
        timing={missedTiming}
        onSend={sendLateWish}
      />
      <MissedWishDialog
        open={cardWish !== null}
        onOpenChange={(open) => {
          if (!open) setCardWish(null);
        }}
        member={cardWish?.member ?? null}
        isBirthdayWish={cardWish?.type !== 'anniversary'}
        timing={cardWish?.timingLabel ?? null}
        onSend={sendCardWish}
        upcoming={cardWish?.timing === 'upcoming'}
        today={cardWish?.timing === 'today'}
      />
      {community.showCelebrations && (
        <div className="space-y-3">
          <SegmentedTabs
            value={activeCelebrationRange}
            onValueChange={(value) => setCelebrationRange(value as CelebrationRange)}
          >
            <SegmentedTabsList
              variant="compact"
              className="w-full flex-nowrap overflow-x-auto border-0 bg-transparent p-0 shadow-none [scrollbar-width:none] sm:w-fit [&::-webkit-scrollbar]:hidden"
            >
              <SegmentedTabsTrigger variant="compact" value="today" className="shrink-0">
                Today
              </SegmentedTabsTrigger>
              <SegmentedTabsTrigger variant="compact" value="week" className="shrink-0">
                This week
              </SegmentedTabsTrigger>
              <SegmentedTabsTrigger variant="compact" value="month" className="shrink-0">
                This month
              </SegmentedTabsTrigger>
              {showNextSevenDays && (
                <SegmentedTabsTrigger variant="compact" value="next7" className="shrink-0">
                  Next 7 days
                </SegmentedTabsTrigger>
              )}
            </SegmentedTabsList>
          </SegmentedTabs>
          <div>
            <div className="flex items-end overflow-x-auto border-b border-border/40 bg-muted/10 [&::-webkit-scrollbar]:hidden">
              {(['birthday', 'anniversary'] as const).map((kind, index) => {
                const isActive = celebrationKind === kind;
                return (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setCelebrationKind(kind)}
                    className={cn(
                      'relative h-9 rounded-t-xl py-2 pl-3 pr-3 text-[13px] font-semibold transition-all sm:h-10 sm:rounded-t-2xl sm:pl-6 sm:pr-6 sm:text-[15px]',
                      isActive
                        ? 'z-10 -mb-px bg-white text-black shadow-[0_2px_8px_rgba(0,0,0,0.4)]'
                        : 'bg-[color-mix(in_hsl,hsl(var(--primary))_22%,hsl(var(--background)))] text-muted-foreground shadow-[0_2px_8px_rgba(0,0,0,0.4)] hover:bg-[color-mix(in_hsl,hsl(var(--primary))_30%,hsl(var(--background)))]'
                    )}
                  >
                    {index === 1 && isActive && (
                      <div className="pointer-events-none absolute bottom-0 -left-2 h-2 w-2 rounded-br-full shadow-[2px_2px_0_2px_white]" />
                    )}
                    <span className="inline-flex items-center gap-1.5">
                      {kind === 'birthday' ? 'Birthday' : 'Anniversary'}
                      {kind === 'birthday' ? (
                        <Cake
                          weight="fill"
                          className={cn(
                            'h-4 w-4',
                            isActive ? 'text-black' : 'text-muted-foreground'
                          )}
                        />
                      ) : (
                        <PartyPopper
                          className={cn(
                            'h-4 w-4',
                            isActive ? 'text-black' : 'text-muted-foreground'
                          )}
                        />
                      )}
                    </span>
                    <div
                      className={cn(
                        'pointer-events-none absolute bottom-0 -right-2 h-2 w-2 rounded-bl-full',
                        isActive
                          ? 'shadow-[-2px_2px_0_2px_white]'
                          : 'shadow-[-2px_2px_0_2px_color-mix(in_hsl,hsl(var(--primary))_22%,hsl(var(--background)))]'
                      )}
                    />
                  </button>
                );
              })}
            </div>
            <div className="overflow-hidden rounded-b-2xl border border-t-0 border-border/50 bg-white shadow-sm">
              {celebrationsLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                </div>
              ) : celebrationRowItems.length > 0 ? (
                <div className="flex flex-col pt-4">
                  {celebrationRowItems.map((row, index) => {
                    const separated = index > 0;
                    if (row.type === 'pair') {
                      return (
                        <div
                          key={`${celebrationEntryKey(row.left)}-${celebrationEntryKey(row.right)}`}
                          className={cn(separated && 'mt-3 border-t border-gray-400 pt-4')}
                        >
                          <CelebrationItem
                            member={row.left.member}
                            type={row.left.type}
                            occasionDate={row.left.date}
                            snug
                            onNameClick={() => promptCardWish(row.left)}
                          />
                          <CelebrationItem
                            member={row.right.member}
                            type={row.right.type}
                            occasionDate={row.right.date}
                            snug
                            onNameClick={() => promptCardWish(row.right)}
                          />
                        </div>
                      );
                    }
                    return (
                      <div
                        key={celebrationEntryKey(row.entry)}
                        className={cn(separated && 'mt-3 border-t border-gray-400 pt-4')}
                      >
                        <CelebrationItem
                          member={row.entry.member}
                          type={row.entry.type}
                          occasionDate={row.entry.date}
                          onNameClick={() => promptCardWish(row.entry)}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="px-4 py-6 text-sm text-muted-foreground">
                  {emptyCelebrationCopy(celebrationKind, activeCelebrationRange)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <UpcomingEventsSection
        communityId={community.id}
        enabled={!!community.showUpcomingEventsInSocialDashboard}
      />

      {/* LEADERSHIP POST SECTION */}
      {isMember && latestInformativePost && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-5"
          aria-labelledby="featured-heading"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h3
                id="featured-heading"
                className="text-2xl font-bold tracking-tight text-foreground"
              >
                Featured
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-md">
                Leadership updates and informative posts for your community.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleOpenHistory}
              className="gap-2 shrink-0 rounded-xl border-border text-muted-foreground hover:text-foreground"
            >
              <History className="h-4 w-4" />
              <span className="text-xs font-semibold">History</span>
            </Button>
          </div>
          {informativeLoading ? (
            <div className="flex items-center justify-center py-12 rounded-2xl border border-dashed border-border/70 bg-muted/15">
              <Loader2 className="h-8 w-8 animate-spin text-primary opacity-60" />
            </div>
          ) : (
            <InformativePostCard post={latestInformativePost} communityId={community.id} />
          )}
        </motion.section>
      )}

      {/* EXISTING FEED */}
      <div className="grid gap-8 grid-cols-1 lg:grid-cols-7">
        <div className="col-span-1 lg:col-span-4 space-y-4 relative">
          {newerCount > 0 && (
            <div className="sticky top-3 z-20 flex justify-center pointer-events-none">
              <Button
                type="button"
                size="sm"
                className="pointer-events-auto rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/25 px-4"
                onClick={() => {
                  setRefreshToken((n) => n + 1);
                  setNewerCount(0);
                }}
              >
                {newerCount === 1 ? '1 new post' : `${newerCount} new posts`}
              </Button>
            </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between mb-2">
            <h3 className="text-2xl font-bold tracking-tight text-foreground">Latest Updates</h3>
            {activeUnreadPostIds.length > 0 && (
              <button
                type="button"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline self-start"
                onClick={() => setUnreadFocusToken((n) => n + 1)}
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
                </span>
                {activeUnreadPostIds.length === 1
                  ? '1 new for you'
                  : `${activeUnreadPostIds.length} new for you`}
              </button>
            )}
          </div>
          <Feed
            communityId={community.id}
            unreadPostIds={activeUnreadPostIds}
            onUnreadViewed={handleUnreadViewed}
            onSessionIdChange={setFeedSessionId}
            refreshToken={refreshToken}
            onRefreshed={() => setNewerCount(0)}
            unreadFocusToken={unreadFocusToken}
          />
        </div>
      </div>

      {/* HISTORY DIALOG */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto border-border">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-foreground">Post history</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {historyLoading && historyPosts.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary opacity-50" />
              </div>
            ) : historyPosts.length === 0 ? (
              <div className="text-center py-14 px-4 rounded-2xl border border-dashed border-border/70 bg-muted/15">
                <p className="font-medium text-foreground">No past posts</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Older featured posts show up here.
                </p>
              </div>
            ) : (
              <>
                {historyPosts.map((p) => (
                  <div
                    key={p.id}
                    className="p-4 rounded-2xl border border-border bg-card/70 dark:bg-muted/20 shadow-sm space-y-2"
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-xs font-semibold',
                          getInformativePostLabelClasses(p.label)
                        )}
                      >
                        {p.label}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(p.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    <div
                      className="text-sm text-foreground prose prose-sm dark:prose-invert max-w-none prose-p:my-1"
                      dangerouslySetInnerHTML={{ __html: p.content }}
                    />
                    {p.image && (
                      <img
                        src={p.image}
                        alt=""
                        className="rounded-xl max-h-32 w-full object-cover border border-border/60"
                      />
                    )}
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Heart className="h-3 w-3" />
                        {p.likeCount}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageSquare className="h-3 w-3" />
                        {p.commentCount}
                      </span>
                    </div>
                  </div>
                ))}
                {historyHasMore && (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => historyCursor && fetchHistory(historyCursor)}
                    disabled={historyLoading}
                  >
                    {historyLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Load More'}
                  </Button>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
